import React, { useCallback, useState, useEffect } from 'react'
import _ from "lodash";
import styles from '../../Style/LegendScaleSlider.module.scss';
import { UndoOutlined } from '@ant-design/icons';
import { InputNumber, Slider, Tooltip, Typography } from 'antd'
import { mapRange } from '../../../../../utils/legendUtils'

interface IMinMax {
  legendMinValue: number;
  legendMaxValue: number;
}

interface LegendScaleSliderProps {
  showLegendScale: boolean;
  startValue: number,
  endValue: number,
  setStartValue: (val: number) => void,
  setEndValue: (val: number) => void,
  legendMinMaxValues: IMinMax;
  initialLegendValue: IMinMax;
  onLegendStartEndChanged: (min: number, max: number) => void;
}

const LEGEND_MIN_VALUE = -10

const LegendScaleSlider = ({
  showLegendScale,
  legendMinMaxValues,
  setStartValue,
  setEndValue,
  startValue,
  endValue,
  initialLegendValue,
  onLegendStartEndChanged
}: LegendScaleSliderProps) => {

  const { legendMaxValue } = legendMinMaxValues

  const [legendSlideValue, setLegendSlideValue] = useState<[number, number]>([0, 100])
  
  useEffect(() => {
    const sliderStart = mapRange(startValue, [LEGEND_MIN_VALUE, legendMaxValue], [0, 100])
    const sliderEnd = mapRange(endValue, [LEGEND_MIN_VALUE, legendMaxValue], [0, 100])
    
    const clampedStart = Math.max(0, Math.min(100, sliderStart))
    const clampedEnd = Math.max(0, Math.min(100, sliderEnd))
    
    setLegendSlideValue([clampedStart, clampedEnd])
  }, [startValue, endValue, LEGEND_MIN_VALUE, legendMaxValue])
  
  const handleLegendSlideChange = (values: [number, number]) => {
    const startVal = mapRange(values[0], [0, 100], [LEGEND_MIN_VALUE, legendMaxValue])
    const endVal = mapRange(values[1], [0, 100], [LEGEND_MIN_VALUE, legendMaxValue])

    const [sliderStart, sliderEnd] = values
    
    if (!_.inRange(sliderStart, 0, 101) || !_.inRange(sliderEnd, 0, 101)) {
      return
    }
    if (sliderStart > sliderEnd) {
      return
    }
    
    const finalStartVal = Math.max(LEGEND_MIN_VALUE, Number(startVal.toFixed(1)))
    const finalEndVal = Math.max(LEGEND_MIN_VALUE, Math.min(legendMaxValue, Number(endVal.toFixed(1))))

    setLegendSlideValue([sliderStart, sliderEnd])
    setStartValue(finalStartVal)
    setEndValue(finalEndVal)
    onLegendStartEndChanged(finalStartVal, finalEndVal)
  }

  const handleLegendStartChange = useCallback((value: number | null) => {
    if (value === null) return
    
    const finalValue = Number(value.toFixed(1))
    const clampedValue = Math.max(LEGEND_MIN_VALUE, Math.min(legendMaxValue, finalValue))
    const actualClampedValue = Math.min(clampedValue, endValue)
    
    setStartValue(actualClampedValue)
    onLegendStartEndChanged(actualClampedValue, endValue)
    
    const sliderStartVal = mapRange(actualClampedValue, [LEGEND_MIN_VALUE, legendMaxValue], [0, 100])
    const sliderEndVal = mapRange(endValue, [LEGEND_MIN_VALUE, legendMaxValue], [0, 100])
    
    setLegendSlideValue([
      Math.max(0, Math.min(100, sliderStartVal)), 
      Math.max(0, Math.min(100, sliderEndVal))
    ])
  }, [endValue, LEGEND_MIN_VALUE, legendMaxValue, LEGEND_MIN_VALUE])
  
  const handleLegendEndChange = useCallback((value: number | null) => {
    if (value === null) return
    
    const finalValue = Number(value.toFixed(1))
    const clampedValue = Math.max(LEGEND_MIN_VALUE, Math.min(legendMaxValue, finalValue))
    const actualClampedValue = Math.max(clampedValue, startValue)
    
    setEndValue(actualClampedValue)
    onLegendStartEndChanged(startValue, actualClampedValue)
    
    const sliderStartVal = mapRange(startValue, [LEGEND_MIN_VALUE, legendMaxValue], [0, 100])
    const sliderEndVal = mapRange(actualClampedValue, [LEGEND_MIN_VALUE, legendMaxValue], [0, 100])
    
    setLegendSlideValue([
      Math.max(0, Math.min(100, sliderStartVal)),
      Math.max(0, Math.min(100, sliderEndVal))
    ])
  }, [startValue, LEGEND_MIN_VALUE, legendMaxValue, LEGEND_MIN_VALUE])

  const handleClearLegend = useCallback(() => {
    const resetStartValue = Math.max(LEGEND_MIN_VALUE, initialLegendValue.legendMinValue)
    const resetEndValue = Math.max(LEGEND_MIN_VALUE, initialLegendValue.legendMaxValue)
    
    setStartValue(resetStartValue)
    setEndValue(resetEndValue)
    onLegendStartEndChanged(resetStartValue, resetEndValue)
    
    const sliderStart = mapRange(resetStartValue, [LEGEND_MIN_VALUE, legendMaxValue], [0, 100])
    const sliderEnd = mapRange(resetEndValue, [LEGEND_MIN_VALUE, legendMaxValue], [0, 100])
    
    setLegendSlideValue([
      Math.max(0, Math.min(100, sliderStart)),
      Math.max(0, Math.min(100, sliderEnd))
    ])
  }, [initialLegendValue, LEGEND_MIN_VALUE, legendMaxValue, setStartValue, setEndValue, onLegendStartEndChanged, LEGEND_MIN_VALUE])

  if (!showLegendScale) return null

  return (
    <div className={styles.container}>
      <div className={styles.inputNumberContainer}>
        <div>
          <Tooltip title="Reset Legend Interval">
            <UndoOutlined style={{ color: 'black' }} onClick={handleClearLegend} />
          </Tooltip>
        </div>
        <div className={styles.inputWrapper}>
          <Typography style={{ marginRight: 5, width: 40 }}>
            Min
          </Typography>
          <InputNumber
            value={Math.max(LEGEND_MIN_VALUE, startValue)}
            onChange={handleLegendStartChange}
            min={LEGEND_MIN_VALUE}
            max={legendMaxValue}
            step={0.1}
            precision={1}
            style={{ width: 100 }}
          />
        </div>
        <div className={styles.inputWrapper}>
          <Typography style={{ marginRight: 5, width: 40 }}>
            Max
          </Typography>
          <InputNumber
            value={endValue}
            onChange={handleLegendEndChange}
            min={LEGEND_MIN_VALUE}
            max={legendMaxValue}
            step={0.1}
            precision={1}
            style={{ width: 100 }}
          />
        </div>
      </div>
      <div>
        <Slider
          tooltip={{
            open: false,
          }}
          range
          value={legendSlideValue}
          onChange={handleLegendSlideChange}
        />
      </div>
    </div>
  )
}

export default LegendScaleSlider